import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../Services/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const fetchNotifications = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await api.get('accounts/notifications/');
            setNotifications(response.data);
            setUnreadCount(response.data.filter(n => !n.is_read).length);
        } catch (error) {
            console.error("Error fetching notifications:", error);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) return;

        fetchNotifications();

        // WebSocket for optimized real-time delivery
        const token = localStorage.getItem("accessToken"); // Correct token key
        const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const wsHost = window.location.hostname === "localhost" ? "localhost:8000" : window.location.host;
        const wsUrl = `${wsProtocol}//${wsHost}/ws/notifications/?token=${token}`;

        let socket;
        let pollInterval;

        const connectWS = () => {
            socket = new WebSocket(wsUrl);

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === "notification") {
                        const newNotif = data.notification;
                        setNotifications((prev) => [newNotif, ...prev]);
                        setUnreadCount((prev) => prev + 1);

                        toast.success(newNotif.title, {
                            icon: "🔔",
                            duration: 4000,
                        });
                    }
                } catch (err) {
                    console.error("WS Message Error:", err);
                }
            };

            socket.onclose = () => {
                console.warn("Notification Socket closed. Fallback to polling.");
            };
        };

        connectWS();

        return () => {
            if (socket) socket.close();
        };
    }, [isAuthenticated, fetchNotifications]);

    const markAsRead = async (id) => {
        try {
            await api.patch(`accounts/notifications/${id}/`, { is_read: true });
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.post('accounts/notifications/read-all/');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
        }
    };

    return (
        <NotificationContext.Provider value={{ 
            notifications, 
            unreadCount, 
            markAsRead, 
            markAllAsRead,
            fetchNotifications 
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);
